import { useBlockErrorTracking } from '@/hooks/useEvidenceAnalytics';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import { AlertCircle, ChevronRight, FilePenLine, Loader2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

interface OpenTemplateEditorBlockProps {
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  error?: string;
  className?: string;
  /**
   * Opens the template. Required: the card has one caller and no fallback, so
   * a host that forgets to wire it should not compile rather than render a
   * dead Open button.
   */
  onOpenTemplate: (templateId: string) => void;
}

/**
 * Renders an agent `open_template_editor` tool call. On success it offers an
 * Open action that focuses the read-only in-chat template preview (the same
 * path the tool's auto-open uses). Manual edits require its full-editor CTA.
 */
export const OpenTemplateEditorBlock = ({
  status,
  templateId,
  templateName,
  error,
  className,
  onOpenTemplate,
}: OpenTemplateEditorBlockProps): JSX.Element => {
  const intl = useIntl();
  useBlockErrorTracking('open_template_editor', status, error);

  if (status === 'applying') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="open-template-editor-block"
        data-status="applying"
      >
        <FilePenLine className="size-5 shrink-0 text-text-tertiary" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            {templateName ?? (
              <FormattedMessage defaultMessage="Template" id="3JxaKsUDfq" />
            )}
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              <FormattedMessage
                defaultMessage="Opening template preview"
                id="rwTJy5KdCy"
              />
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="open-template-editor-block"
        data-status="error"
      >
        <AlertCircle
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            <FormattedMessage defaultMessage="Couldn't open template" id="Oq8mmPq2Vn" />
          </TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage
                defaultMessage="Something went wrong while opening the template."
                id="ogBeIYQjfX"
              />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  const resolvedName =
    templateName ??
    intl.formatMessage({
      defaultMessage: 'Template',
      id: '3JxaKsUDfq',
    });

  const canOpen = Boolean(templateId);

  const handleOpen = () => {
    if (!templateId) return;
    onOpenTemplate(templateId);
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="open-template-editor-block"
      data-status="completed"
      data-template-id={templateId}
      data-template-name={resolvedName}
    >
      <FilePenLine className="size-5 shrink-0 text-text-secondary" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyPBold className="line-clamp-2 text-text-primary">
          <FormattedMessage
            defaultMessage="{name} opened for preview"
            id="bJqI8iloRg"
            values={{ name: resolvedName }}
          />
        </TypographyPBold>
        <TypographyCaption className="text-text-tertiary">
          <FormattedMessage
            defaultMessage="Review the template preview. To make edits, choose “Open in full editor”."
            id="JpFJ4PaFAY"
          />
        </TypographyCaption>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            flush="all"
            className="text-text-secondary"
            onClick={handleOpen}
            disabled={!canOpen}
          >
            <FormattedMessage defaultMessage="Open" id="JfG49wNHKP" />
            <ChevronRight className="rtl:-scale-x-100" />
          </Button>
        </div>
      </div>
    </div>
  );
};
